import { useEffect, useId, useRef, useState } from 'react'
import { useDismiss } from '../lib/useDismiss'
import { useHubLang } from '../lib/i18n'
import { STARS } from '../lib/hubCopy'
import { PickerFoot } from './PickerFoot'
import { Dialog } from './Dialog'
import {
  ENDORSEMENTS_ALL, MAX_ENDORSEMENTS, sameWords, suggestEndorsements,
  type Endorsement, type EndorsementOption,
} from '../lib/stars'

/**
 * "What for" — the endorsement field. 128, David 30 Sep.
 *
 * ONE COMPONENT, TWO HOSTS: the give form and the wall's in-place editor. Two
 * copies would be two answers to "may I add a fourth" and "is this the same
 * word", which is exactly how the give form and the editor drifted before.
 *
 * ── THE NUDGE IS THE ORDER ON SCREEN ────────────────────────────────────────
 *
 * The featured official endorsements are chips first, because pressing one is
 * the cheapest thing on the form. Typing opens what ALREADY exists — official
 * first, then what people use, with how often — and only then, last in the
 * list, "Add “…”". Nothing refuses a new word; the screen just makes the
 * existing one the easier thing to reach. That is the whole of the design:
 * no admin in front of the vocabulary, and a vocabulary that still converges.
 *
 * ── THE VALUE IS WORDS, NOT IDS ─────────────────────────────────────────────
 *
 * What goes to the server is the label as picked or typed; the server resolves
 * it (own slug, then any official label in any locale) and coins a community
 * word only when nothing matches. So a Spanish speaker pressing "Liderazgo"
 * and an English one typing "leadership" land on one row, and nothing here
 * has to know that. `sameWords` only stops one chip being pressed twice.
 *
 * ── THE LIST IS THE SERVER'S PAGE ───────────────────────────────────────────
 *
 * An option already chosen is SHOWN as chosen, not dropped: dropping it would
 * be filtering a limited page (standing rule 1), and the foot's "N of M" would
 * then describe a list that is not on screen.
 *
 * ── A PAGE OFFERS THE REST; IT DOES NOT JUST SAY THEY EXIST (336) ──────────
 *
 * The panel is the server's first 8. When more matched, its foot is a
 * "Show all N" button, not a sentence — David, 30 Oct: the old foot said
 * "Showing 8 of 14 people — type a name to narrow the list", which was the
 * person pickers' wording on a list of words, and a dead end besides. The
 * button opens `AllEndorsements`: every match, scrolling, searchable, carrying
 * whatever was already typed.
 */
export function EndorsementField({
  featured,
  value,
  onChange,
}: {
  featured: Endorsement[]
  /** The chosen words, in order. */
  value: string[]
  onChange: (next: string[]) => void
}) {
  const lang = useHubLang()
  const t = STARS[lang]
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<EndorsementOption[]>([])
  const [total, setTotal] = useState(0)
  const [active, setActive] = useState(0)
  const [all, setAll] = useState(false)
  const wrapRef = useRef<HTMLFieldSetElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const full = value.length >= MAX_ENDORSEMENTS
  const has = (w: string) => value.some((v) => sameWords(v, w))
  const typed = query.trim().replace(/^#+/, '').trim()
  // "Add" is offered when something is typed and nothing on the page is that
  // word. It is the LAST row, after everything that already exists.
  const offerAdd = Boolean(typed) && !options.some((o) => sameWords(o.label, typed))
  const rows = options.length + (offerAdd ? 1 : 0)

  useDismiss(wrapRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) inputRef.current?.focus()
  })

  useEffect(() => {
    if (!open) return
    let live = true
    const timer = setTimeout(() => {
      suggestEndorsements(query)
        .then((page) => {
          if (!live) return
          setOptions(page.options)
          setTotal(page.total)
          setActive(0)
        })
        .catch(() => {
          if (!live) return
          setOptions([])
          setTotal(0)
        })
    }, 150)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [query, open])

  function add(word: string) {
    const w = word.trim().replace(/^#+/, '').replace(/\s+/g, ' ').trim()
    if (!w || has(w) || full) return
    onChange([...value, w])
    setQuery('')
    setOpen(false)
  }

  function remove(word: string) {
    onChange(value.filter((v) => !sameWords(v, word)))
  }

  function toggle(word: string) {
    if (has(word)) remove(word)
    else add(word)
  }

  function openAll() {
    setOpen(false)
    setAll(true)
  }

  function closeAll() {
    setAll(false)
    setQuery('')
    // Dialog returns focus to what opened it, but that was the "Show all"
    // button, which went with the panel. The input is where the person was.
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  // Chosen words that are not one of the featured chips get a removable chip
  // of their own; a featured one shows as its chip pressed.
  const extra = value.filter((v) => !featured.some((f) => sameWords(f.label, v)))

  return (
    <fieldset className="stars__field endorse" ref={wrapRef}>
      <legend className="composer__label">{t.whatFor}</legend>
      <p className="endorse__hint">{t.endorseHint}</p>

      {(featured.length > 0 || extra.length > 0) && (
        <div className="chips">
          {featured.map((f) => {
            const on = has(f.label)
            return (
              <button
                key={f.id}
                type="button"
                className={`chip${on ? ' chip--on' : ''}`}
                aria-pressed={on}
                disabled={!on && full}
                onClick={() => toggle(f.label)}
              >
                {f.label}
              </button>
            )
          })}
          {extra.map((w) => (
            <span className="chip chip--value" key={w.toLocaleLowerCase()}>
              {w}
              <button
                type="button"
                className="chip__x"
                aria-label={t.endorseRemove(w)}
                onClick={() => remove(w)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {full ? (
        <p className="endorse__hint" role="status">{t.endorseMax}</p>
      ) : (
        <div className="endorse__find">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            className="endorse__input"
            aria-label={t.endorsePlaceholder}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && rows ? `${listId}-${active}` : undefined}
            placeholder={t.endorsePlaceholder}
            maxLength={40}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Enter never submits the whole form from here: it picks.
                e.preventDefault()
                if (open && active < options.length && options[active]) add(options[active].label)
                else if (typed) add(typed)
                return
              }
              if (!open || rows === 0) return
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((i) => (i + 1) % rows)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((i) => (i - 1 + rows) % rows)
              }
            }}
          />
          {open && (
            <div className="picker__panel picker__panel--endorse">
              {rows === 0 ? (
                <p className="picker__none">{t.endorseNothing}</p>
              ) : (
                <ul className="picker__list" id={listId} role="listbox">
                  {options.map((o, i) => {
                    const chosen = has(o.label)
                    return (
                      <li
                        key={o.endorsementId}
                        id={`${listId}-${i}`}
                        role="option"
                        aria-selected={i === active}
                        aria-disabled={chosen}
                        data-active={i === active}
                        className="picker__opt picker__opt--endorse"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          if (!chosen) add(o.label)
                        }}
                      >
                        <EndorsementCols option={o} chosen={chosen} />
                      </li>
                    )
                  })}
                  {offerAdd && (
                    <li
                      id={`${listId}-${options.length}`}
                      role="option"
                      aria-selected={active === options.length}
                      data-active={active === options.length}
                      className="picker__opt endorse__add"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        add(typed)
                      }}
                    >
                      <span className="picker__optlabel">{t.endorseAdd(typed)}</span>
                    </li>
                  )}
                </ul>
              )}
              {total > options.length && (
                <div className="picker__foot endorse__more">
                  <button
                    type="button"
                    className="btn btn--quiet btn--sm"
                    // Not onMouseDown-only: a keyboard user reaches this by Tab.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openAll}
                  >
                    {t.endorseShowAll(total)}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <AllEndorsements
        open={all}
        initialQuery={query}
        chosen={value}
        onToggle={toggle}
        onAdd={add}
        onClose={closeAll}
      />
    </fieldset>
  )
}

/**
 * One option's cells, the same in the panel and in "Show all" (336). Three
 * fixed columns — the word, "Official", how often it is used — each one line,
 * cut with an ellipsis and whole on hover: the person picker's recipe (335,
 * David 30 Oct: "columns fixed"), so the eye runs down a column instead of
 * hunting for where each row's detail starts. The word column holds 22
 * characters, the longest label on dev and prod (30 Oct).
 */
function EndorsementCols({ option: o, chosen }: { option: EndorsementOption; chosen: boolean }) {
  const t = STARS[useHubLang()]
  const uses = o.uses > 0 ? t.endorseUses(o.uses) : ''
  return (
    <>
      <span className="picker__endorseword" title={o.label}>
        {o.label}
        {chosen && ' ✓'}
      </span>
      <span className="picker__endorsecol">{o.isOfficial ? t.endorseOfficial : ''}</span>
      <span className="picker__endorsecol" title={uses || undefined}>{uses}</span>
    </>
  )
}

/**
 * "Show all" (336). Every word that matches, in the server's order, in a
 * modal that scrolls and searches.
 *
 * THE SEARCH IS THE SERVER'S, as in the panel: `suggest_endorsements` with the
 * most it will return (50, 128's cap). A client-side filter over a fetched page
 * would be standing rule 1 again the day there are more than 50 words, so past
 * that the foot says the list is a page, in words that fit words.
 *
 * ROWS TOGGLE AND THE MODAL STAYS. A recognition carries up to three, so
 * closing on the first press would mean reopening it twice. "Done", ×, Escape
 * and the scrim all close it; the chips behind already show what was picked.
 *
 * FOCUS GOES TO THE SEARCH, not the panel. Dialog focuses its panel so a form's
 * title is heard first; here the title is announced as the dialog's name on the
 * field, and the one thing anybody opens this to do is type.
 *
 * TYPING HERE IS NOT WORK TO PROTECT. Dialog refuses a scrim click once
 * something is typed inside it, and the event bubbles through the React tree to
 * the give dialog behind, too. A search term is not a draft, so its `input`
 * stops at the search box.
 */
function AllEndorsements({
  open,
  initialQuery,
  chosen,
  onToggle,
  onAdd,
  onClose,
}: {
  open: boolean
  initialQuery: string
  chosen: string[]
  onToggle: (word: string) => void
  onAdd: (word: string) => void
  onClose: () => void
}) {
  const lang = useHubLang()
  const t = STARS[lang]
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<EndorsementOption[]>([])
  const [total, setTotal] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  const full = chosen.length >= MAX_ENDORSEMENTS
  const has = (w: string) => chosen.some((v) => sameWords(v, w))
  const typed = query.trim().replace(/^#+/, '').trim()
  const offerAdd = Boolean(typed) && loaded && !has(typed) && !options.some((o) => sameWords(o.label, typed))

  useEffect(() => {
    if (!open) return
    setQuery(initialQuery)
    setLoaded(false)
    // After Dialog's own effect has focused its panel (a child's effects run
    // first), so this one is the focus that stays.
    searchRef.current?.focus()
    // initialQuery is read once, as the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    let live = true
    const timer = setTimeout(() => {
      suggestEndorsements(query, ENDORSEMENTS_ALL)
        .then((page) => {
          if (!live) return
          setOptions(page.options)
          setTotal(page.total)
          setLoaded(true)
        })
        .catch(() => {
          if (!live) return
          setOptions([])
          setTotal(0)
          setLoaded(true)
        })
    }, 150)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [query, open])

  return (
    <Dialog open={open} title={t.whatFor} onClose={onClose} className="endorseall">
      <div className="picker__search endorseall__search">
        <input
          ref={searchRef}
          type="search"
          aria-label={t.endorsePlaceholder}
          placeholder={t.endorsePlaceholder}
          maxLength={40}
          value={query}
          onInput={(e) => e.stopPropagation()}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // The give form is this modal's React parent: Enter must not submit it.
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (offerAdd && !full) onAdd(typed)
          }}
        />
      </div>
      {loaded && options.length === 0 && !offerAdd ? (
        <p className="picker__none">{t.endorseNothing}</p>
      ) : (
        <ul className="picker__list endorseall__list">
          {options.map((o) => {
            const on = has(o.label)
            return (
              <li key={o.endorsementId}>
                <button
                  type="button"
                  className="picker__opt picker__opt--endorse"
                  aria-pressed={on}
                  disabled={!on && full}
                  onClick={() => onToggle(o.label)}
                >
                  <EndorsementCols option={o} chosen={on} />
                </button>
              </li>
            )
          })}
          {offerAdd && (
            <li>
              <button
                type="button"
                className="picker__opt endorse__add"
                disabled={full}
                onClick={() => onAdd(typed)}
              >
                <span className="picker__optlabel">{t.endorseAdd(typed)}</span>
              </button>
            </li>
          )}
        </ul>
      )}
      <PickerFoot shown={options.length} total={total} hasQuery={Boolean(query.trim())} kind="words" />
      <div className="endorseall__foot">
        <span className="endorseall__count" role="status">
          {full ? t.endorseMax : t.endorseChosen(chosen.length, MAX_ENDORSEMENTS)}
        </span>
        <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>
          {t.endorseDone}
        </button>
      </div>
    </Dialog>
  )
}
